import { FormEvent, useEffect, useState } from "react";
import { assets } from "../../lib/assets";
import {
  ensureLearningProfile,
  getPrivateAvatarUrl,
  recordAccountEvent,
  registerWithUsername,
  signInWithUsername,
  type ChildGender,
  type SchoolStage
} from "../../services/auth/account";
import { supabase } from "../../services/supabase/client";
import { useAppStore } from "../../store/useAppStore";

type Mode = "login" | "register";
type RegisterStep = 1 | 2 | 3;

const stageOptions: Array<{ value: SchoolStage; label: string }> = [
  { value: "", label: "暂不设置" },
  { value: "kindergarten_small", label: "幼儿园小班" },
  { value: "kindergarten_middle", label: "幼儿园中班" },
  { value: "kindergarten_large", label: "幼儿园大班" },
  { value: "preschool", label: "学前阶段" },
  { value: "primary_1", label: "小学一年级" }
];

export function AuthGate({ children }: { children: React.ReactNode }) {
  const setActiveChild = useAppStore((state) => state.setActiveChild);
  const setChildProfile = useAppStore((state) => state.setChildProfile);
  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [mode, setMode] = useState<Mode>("login");
  const [registerStep, setRegisterStep] = useState<RegisterStep>(1);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [childName, setChildName] = useState("");
  const [gender, setGender] = useState<ChildGender>("unspecified");
  const [birthDate, setBirthDate] = useState("");
  const [schoolStage, setSchoolStage] = useState<SchoolStage>("");
  const [message, setMessage] = useState("使用用户名和密码，成长记录会安全保存到云端。");
  const [messageTone, setMessageTone] = useState<"info" | "error">("info");
  const [busy, setBusy] = useState(false);

  async function activateChild(child: { id: string; nickname: string; avatar_storage_key?: string | null }) {
    const avatarUrl = await getPrivateAvatarUrl(child.avatar_storage_key);
    setActiveChild(child.id, child.nickname);
    setChildProfile(child.nickname, avatarUrl);
    localStorage.setItem("lusky:active-profile", JSON.stringify({ id: child.id, nickname: child.nickname, avatarUrl }));
    setReady(true);
  }

  useEffect(() => {
    let alive = true;
    const failSafe = window.setTimeout(() => { if (alive) { setMessage("连接星光档案超时，请重新登录或检查网络。"); setMessageTone("error"); setChecking(false); } }, 5000);
    void (async () => {
      if (!supabase) {
        if (alive) { setMessage("学习记录服务暂不可用，请稍后再试。"); setMessageTone("error"); setChecking(false); }
        return;
      }
      const { data, error } = await supabase.auth.getUser();
      if (error || !data.user) { if (alive) setChecking(false); return; }
      try {
        const child = await ensureLearningProfile(data.user);
        await recordAccountEvent(child.id, "account.session_restored");
        if (alive) await activateChild(child);
      } catch (error) {
        if (alive) { setMessage(error instanceof Error ? `已登录，但档案读取失败：${error.message}` : "已登录，但孩子档案暂时未能读取。"); setMessageTone("error"); }
      } finally { if (alive) setChecking(false); }
    })();
    const { data: listener } = supabase?.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        localStorage.removeItem("lusky:active-profile");
        setReady(false);
        setChecking(false);
        setActiveChild(null);
        setChildProfile("小小探索师", null);
      }
    }) ?? { data: { subscription: null } };
    return () => { alive = false; window.clearTimeout(failSafe); listener.subscription?.unsubscribe(); };
  }, [setActiveChild, setChildProfile]);

  function switchMode(nextMode: Mode) {
    setMode(nextMode);
    setRegisterStep(1);
    setMessageTone("info");
    setMessage(nextMode === "login" ? "使用用户名和密码，成长记录会安全保存到云端。" : "只需三小步，就能创建专属成长空间。");
  }

  function continueRegistration() {
    setMessageTone("error");
    if (registerStep === 1) {
      if (!/^[a-z0-9][a-z0-9._-]{2,31}$/i.test(username.trim())) return setMessage("用户名请使用 3–32 位英文、数字、点、下划线或短横线。");
      if (password.length < 8) return setMessage("为了保护成长档案，注册密码至少需要 8 位。");
      if (password !== confirmPassword) return setMessage("两次输入的密码不一致，请重新确认。");
      setRegisterStep(2);
      setMessageTone("info");
      setMessage("告诉我们该怎样称呼小朋友吧。");
      return;
    }
    if (!childName.trim()) return setMessage("请先填写孩子昵称，也可以使用家庭中的小名。");
    setRegisterStep(3);
    setMessageTone("info");
    setMessage("下面两项可以跳过，后续也能在“我的”页面补充。");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessageTone("info");
    setMessage(mode === "login" ? "正在打开你的星光档案…" : "正在创建专属成长空间…");
    try {
      if (mode === "login") {
        const result = await signInWithUsername(username, password);
        await activateChild(result.child);
      } else {
        const result = await registerWithUsername(username, password, { nickname: childName, gender, birthDate, schoolStage });
        await activateChild(result.child);
      }
    } catch (error) {
      setMessageTone("error");
      setMessage(error instanceof Error ? error.message : "操作没有完成，请再试一次。");
    } finally { setBusy(false); }
  }

  if (ready) return <>{children}</>;
  if (checking) return <main className="auth-page"><div className="auth-loading"><img src={assets.lumi} alt="" /><span>正在寻找你的星光档案…</span></div></main>;

  const registerTitle = registerStep === 1 ? "创建家长账号" : registerStep === 2 ? "认识小小探索师" : "完善成长起点";
  return <main className="auth-page">
    <section className="auth-card-scene">
      <aside className="auth-scene-story">
        <img className="auth-scene-logo" src={assets.logo} alt="Lusky 鹿屿森空" />
        <h1>每一次好奇，<br />都值得被好好守护。</h1>
        <p>家长先点亮星光钥匙，再和露米、阿朵一起开始今天的探索。</p>
        <div className="auth-trust"><span>✓ 独立孩子档案</span><span>✓ 学习记录云端同步</span><span>✓ 头像私密存储</span></div>
        <img className="auth-scene-hero" src={assets.hero} alt="鹿屿森空主角" />
        <img className="auth-scene-lumi" src={assets.lumi} alt="露米" />
      </aside>
      <section className="auth-card-form">
        <p className="auth-kicker">鹿屿森空 · 家长入口</p>
        <h2>{mode === "login" ? "欢迎回到鹿屿森空" : registerTitle}</h2>
        <p className="auth-description">{mode === "login" ? "登录后继续上次的学习、星光与探索记录。" : "孩子无需填写真实姓名，带 * 的项目才是必填项。"}</p>
        <div className="auth-switch" aria-label="登录或注册">
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => switchMode("login")}>登录</button>
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => switchMode("register")}>注册</button>
        </div>

        {mode === "register" && <div className="auth-steps" aria-label={`注册进度，第 ${registerStep} 步，共 3 步`}>
          {[1, 2, 3].map((step) => <span key={step} className={step <= registerStep ? "active" : ""}><b>{step}</b><em>{step === 1 ? "账号" : step === 2 ? "孩子" : "成长"}</em></span>)}
        </div>}

        <form onSubmit={submit}>
          {(mode === "login" || registerStep === 1) && <>
            <label>用户名 {mode === "register" && <i>*</i>}<input value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" placeholder="例如 lusky-family" required /></label>
            <label>密码 {mode === "register" && <i>*</i>}<div className="auth-password"><input value={password} onChange={(event) => setPassword(event.target.value)} type={showPassword ? "text" : "password"} autoComplete={mode === "login" ? "current-password" : "new-password"} minLength={mode === "login" ? 6 : 8} placeholder={mode === "login" ? "请输入密码" : "至少 8 位"} required /><button type="button" onClick={() => setShowPassword((value) => !value)}>{showPassword ? "隐藏" : "显示"}</button></div></label>
            {mode === "register" && <label>确认密码 <i>*</i><input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} type={showPassword ? "text" : "password"} autoComplete="new-password" minLength={8} placeholder="再输入一次密码" required /></label>}
          </>}

          {mode === "register" && registerStep === 2 && <>
            <label>孩子昵称 <i>*</i><input value={childName} onChange={(event) => setChildName(event.target.value)} maxLength={32} placeholder="小名或喜欢的称呼" autoFocus required /></label>
            <fieldset className="auth-choice"><legend>性别</legend><div>
              {([{ value: "girl", label: "女孩", icon: "🌷" }, { value: "boy", label: "男孩", icon: "🚀" }, { value: "unspecified", label: "暂不设置", icon: "✨" }] as const).map((item) => <button type="button" key={item.value} className={gender === item.value ? "selected" : ""} onClick={() => setGender(item.value)}><b>{item.icon}</b><span>{item.label}</span></button>)}
            </div></fieldset>
          </>}

          {mode === "register" && registerStep === 3 && <div className="auth-optional-grid">
            <label>出生日期 <small>选填</small><input value={birthDate} onChange={(event) => setBirthDate(event.target.value)} type="date" max={new Date().toISOString().slice(0, 10)} /></label>
            <label>学龄阶段 <small>选填</small><select value={schoolStage} onChange={(event) => setSchoolStage(event.target.value as SchoolStage)}>{stageOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
            <p className="auth-privacy">这些信息用于调整内容难度与称呼，不会展示给其他家庭。</p>
          </div>}

          {mode === "login" && <button className="auth-submit" disabled={busy}>{busy ? "正在进入…" : "登录并进入　→"}</button>}
          {mode === "register" && <div className="auth-form-actions">
            {registerStep > 1 && <button type="button" className="auth-back" onClick={() => setRegisterStep((registerStep - 1) as RegisterStep)}>上一步</button>}
            {registerStep < 3 ? <button type="button" className="auth-submit" onClick={continueRegistration}>继续　→</button> : <button className="auth-submit" disabled={busy}>{busy ? "正在创建…" : "完成注册并进入　→"}</button>}
          </div>}
        </form>
        <p className={`auth-status ${messageTone === "error" ? "is-error" : ""}`} role="status">{message}</p>
      </section>
    </section>
  </main>;
}